import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WalletMonitoringItem, WalletsMonitoringQuery } from '@shared/api/contracts';
import {
  OPERATION_WALLET_QUERY_KINDS,
  SERVICE_WALLET_KINDS,
  sortWalletsForAccountingOperation,
} from '@entities/service-accounting';
import { monitoringPageCount, WALLET_MONITORING_FETCH_LIMIT } from '@entities/wallets';
import { useApiServices } from '@shared/lib/use-api-services';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_INITIAL_PAGES = 2;
const SEARCH_INITIAL_PAGES = 1;

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

export const buildOperationWalletsQuery = (search: string, page = 1): WalletsMonitoringQuery => {
  const base = {
    page,
    limit: WALLET_MONITORING_FETCH_LIMIT,
  };

  if (search) {
    return {
      ...base,
      walletKinds: [...OPERATION_WALLET_QUERY_KINDS],
      search,
    };
  }

  return {
    ...base,
    walletKinds: [...SERVICE_WALLET_KINDS],
  };
};

const mergeWalletPages = (
  existing: WalletMonitoringItem[],
  incoming: WalletMonitoringItem[],
): WalletMonitoringItem[] => {
  const seen = new Set(existing.map((wallet) => String(wallet.id)));
  const uniqueIncoming = incoming.filter((wallet) => !seen.has(String(wallet.id)));
  if (!uniqueIncoming.length) return existing;
  return sortWalletsForAccountingOperation([...existing, ...uniqueIncoming]);
};

export const useWalletsForOperation = (enabled: boolean, search: string) => {
  const services = useApiServices();
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [wallets, setWallets] = useState<WalletMonitoringItem[]>([]);
  const [loadedPage, setLoadedPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const initialPageCount = debouncedSearch ? SEARCH_INITIAL_PAGES : DEFAULT_INITIAL_PAGES;
  const isSearchPending = search.trim() !== debouncedSearch;

  useEffect(() => {
    if (!enabled) {
      setWallets([]);
      setLoadedPage(0);
      setTotalPages(1);
      setHasLoadedOnce(false);
      setError(undefined);
      return;
    }

    let cancelled = false;

    const loadInitial = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const firstQuery = buildOperationWalletsQuery(debouncedSearch, 1);
        const first = await services.wallets.getWalletsMonitoring(firstQuery);
        if (cancelled) return;

        const total = monitoringPageCount(first.pagination?.totalPages);
        setTotalPages(total);

        const pagesToLoad = Math.min(initialPageCount, total);
        let merged = sortWalletsForAccountingOperation(first.data ?? []);

        if (pagesToLoad > 1) {
          const rest = await Promise.all(
            Array.from({ length: pagesToLoad - 1 }, (_, index) =>
              services.wallets.getWalletsMonitoring(
                buildOperationWalletsQuery(debouncedSearch, index + 2),
              ),
            ),
          );
          if (cancelled) return;
          for (const page of rest) {
            merged = mergeWalletPages(merged, page.data ?? []);
          }
        }

        setWallets(merged);
        setLoadedPage(pagesToLoad);
        setHasLoadedOnce(true);
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadInitial();

    return () => {
      cancelled = true;
    };
  }, [enabled, debouncedSearch, initialPageCount, services.wallets]);

  const loadMore = useCallback(async () => {
    if (!enabled || isLoading || isLoadingMore || loadedPage >= totalPages) return;

    setIsLoadingMore(true);
    try {
      const nextPage = loadedPage + 1;
      const response = await services.wallets.getWalletsMonitoring(
        buildOperationWalletsQuery(debouncedSearch, nextPage),
      );
      setTotalPages(monitoringPageCount(response.pagination?.totalPages));
      setWallets((current) => mergeWalletPages(current, response.data ?? []));
      setLoadedPage(nextPage);
    } catch (nextError) {
      setError(nextError);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    debouncedSearch,
    enabled,
    isLoading,
    isLoadingMore,
    loadedPage,
    services.wallets,
    totalPages,
  ]);

  const hasMore = useMemo(
    () => enabled && loadedPage > 0 && loadedPage < totalPages,
    [enabled, loadedPage, totalPages],
  );

  const isInitialLoading = (isLoading || isSearchPending) && !hasLoadedOnce;
  const isListLoading = (isLoading || isSearchPending) && !isLoadingMore;

  return {
    wallets,
    isInitialLoading,
    isListLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
  };
};
